import { csrfFetch } from './csrf.js';
import { gcoreServerUrl } from '../config';

const SET_SUB_MAT_TEMPLATES = 'subMatTemplate/setSubMatTemplates';
const SET_SUB_MAT_TEMPLATE = 'subMatTemplate/setSubMatTemplate';

const setSubMatTemplates = (payload) => ({
  type: SET_SUB_MAT_TEMPLATES,
  payload,
});

const setSubMatTemplate = (subMatTemplate) => ({
  type: SET_SUB_MAT_TEMPLATE,
  payload: subMatTemplate,
});

export const selectAll = () => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates`);
  const data = await response.json();
  dispatch(setSubMatTemplates(data));
  return data;
};

export const select = (id) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/${id}`);
  const data = await response.json();
  dispatch(setSubMatTemplate(data.subMatTemplate));
  return data.subMatTemplate;
};

export const create = (payload) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  dispatch(setSubMatTemplate(data.subMatTemplate));
  return data.subMatTemplate;
};

export const modify = (payload) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  dispatch(setSubMatTemplate(data.subMatTemplate));
  return data.subMatTemplate;
};

export const remove = (id) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/${id}`, {
    method: 'DELETE',
  });
  return response;
};

export const buildItems = (folderIds) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/buildItems`, {
    method: 'POST',
    body: JSON.stringify({ folderIds }),
  });
  const data = await response.json();
  return data.subMatTemplateItems || [];
};

/** 목차 합본 PDF 미리보기 다운로드 (서버 비저장). returns Response for blob() */
export const previewMerge = (payload) => async (dispatch) => {
  const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/previewMerge`, {
    method: 'POST',
    body: JSON.stringify({
      name: payload?.name || '',
      tocNumbering: payload?.tocNumbering || 'DECIMAL_HIERARCHY',
      bodyHeaderPaging: payload?.bodyHeaderPaging !== false,
      items: payload?.items || [],
      folderOrder: payload?.folderOrder || [],
    }),
  });
  if (!response.ok) {
    let message = '합본 PDF를 만들지 못했습니다.';
    try {
      const err = await response.json();
      message = err.message || err.title || message;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }
  return response;
};

/**
 * 발송 직전 합본 PDF 미리보기 (별도 첨부·원본대조필). returns Response for blob()
 * - 별도 첨부 없음: JSON (템플릿 수정의 previewMerge 와 동일 전송 방식)
 * - 별도 첨부 있음: multipart mailData + addedFiles
 */
export const previewSendMerge = ({ mailData, addedFiles }) => async (dispatch) => {
  const files = (addedFiles || []).filter(Boolean);
  const url = `${gcoreServerUrl}/api/subMatTemplates/previewSendMerge`;

  let response;
  if (!files.length) {
    response = await csrfFetch(dispatch, url, {
      method: 'POST',
      body: JSON.stringify(mailData || {}),
    });
  } else {
    const formData = new FormData();
    formData.append('mailData', JSON.stringify(mailData || {}));
    files.forEach((file) => {
      formData.append('addedFiles', file);
    });
    response = await csrfFetch(dispatch, url, {
      method: 'POST',
      body: formData,
    });
  }

  if (!response.ok) {
    let message = '합본 PDF를 만들지 못했습니다.';
    try {
      const err = await response.json();
      message = err.message || err.title || message;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }
  return response;
};

/**
 * 템플릿 합본 PDF 발송 (템플릿 DB 미갱신).
 * mailData: JSON fields; addedFiles: File[] (별도 첨부 PDF)
 */
export const send = ({ mailData, addedFiles }) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('mailData', JSON.stringify(mailData || {}));
    (addedFiles || []).forEach((file) => {
      formData.append('addedFiles', file);
    });

    const response = await csrfFetch(dispatch, `${gcoreServerUrl}/api/subMatTemplates/send`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.message || data.title || '발송에 실패했습니다.';
      throw Object.assign(new Error(message), { serverResponse: data, response });
    }
    return data;
  } catch (e) {
    const message = e?.message
      || e?.serverResponse?.message
      || e?.title
      || '발송에 실패했습니다.';
    throw new Error(message);
  }
};

const initialState = {
  subMatTemplates: [],
  count: 0,
  subMatTemplate: null,
};

function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_SUB_MAT_TEMPLATES:
      return {
        ...state,
        subMatTemplates: action.payload.subMatTemplates || [],
        count: action.payload.count ?? 0,
      };
    case SET_SUB_MAT_TEMPLATE:
      return { ...state, subMatTemplate: action.payload };
    default:
      return state;
  }
}

export default reducer;
